import { ref } from 'vue';
import { httpService } from '@/services/httpService';
import { config } from '@/services/configService';
import { log } from '@/utils/DebugLogger';

import type { Galeria_GetImagesWithoutThumbnailsResponseDados } from '../contracts/Galeria_GetImagesWithoutThumbnailsResponse';
import type { Galeria_GenerateSingleThumbnailResponseDados } from '../contracts/Galeria_GenerateSingleThumbnailResponse';
import type { Galeria_ImageRow } from '../contracts/Galeria_ImageRow';
import '../contracts/Galeria_GenerateMissingThumbnailsResponse';
import '../contracts/Galeria_GenerateSingleThumbnailResponse';
import '../contracts/Galeria_GetImagesWithoutThumbnailsResponse';
import '../contracts/Galeria_ListaImagensResponse';
import '../contracts/ImagensServicos_GetAllServicoImagesResponse';
/**
 * Composable for managing image gallery
 * Displays before/after images from completed service appointments
 */
export function useGaleria() {
  // Progress tracking state for batch thumbnail generation
  const thumbnailProgress = ref({
    isProcessing: false,
    current: 0,
    total: 0,
    created: 0,
    alreadyExist: 0,
    errors: 0,
    errorDetails: [] as string[],
    currentImageName: '',
    isCancelled: false
  });

  /**
   * Fetch all gallery images (before/after photos from completed services)
   * @returns {Promise<Galeria_ImageRow[]>} List of gallery items with image paths
   */
  const fetchGaleriaImages = async (): Promise<Galeria_ImageRow[]> => {
    log('useGaleria', 'fetchGaleriaImages', 'Fetching gallery images');
    try {
      const response = await httpService.get('/index.php/galeria/lista_imagens', { responseContract: 'Galeria_ListaImagensResponse' });
      const data = response.dados?.items ?? [];
      log('useGaleria', 'fetchGaleriaImages', 'Gallery images loaded', data);
      return data;
    } catch (error) {
      log('useGaleria', 'fetchGaleriaImages', 'Error loading gallery images', error);
      throw error;
    }
  };

  /**
   * Fetch all images for a specific compromisso_servico_id
   * Returns all antes/depois images for gallery display
   * @param {number} compromissoServicoId - The service ID
   * @returns {Promise<Array>} Array of all images for the service
   */
  const fetchAllServicoImages = async (compromissoServicoId: number) => {
    log('useGaleria', 'fetchAllServicoImages', 'Fetching all images for service', compromissoServicoId);
    try {
      const response = await httpService.get(`/index.php/config/imagens_servicos/get_all_servico_images/${compromissoServicoId}`, { responseContract: 'ImagensServicos_GetAllServicoImagesResponse' });
      log('useGaleria', 'fetchAllServicoImages', 'Images loaded', response.dados);
      return response.dados || [];
    } catch (error) {
      log('useGaleria', 'fetchAllServicoImages', 'Error loading service images', error);
      throw error;
    }
  };

  /**
   * Get list of images that don't have thumbnails yet
   * @returns {Promise<Object>} Object with images array and statistics
   */
  const fetchImagesWithoutThumbnails = async (): Promise<Galeria_GetImagesWithoutThumbnailsResponseDados> => {
    log('useGaleria', 'fetchImagesWithoutThumbnails', 'Fetching images without thumbnails');
    try {
      const response = await httpService.get('/index.php/galeria/get_images_without_thumbnails', { responseContract: 'Galeria_GetImagesWithoutThumbnailsResponse' });
      log('useGaleria', 'fetchImagesWithoutThumbnails', 'Images fetched', response.dados);
      return response.dados || {
        images: [],
        total_needing_thumbnails: 0,
        already_have_thumbnails: 0,
        images_not_found: 0,
        total_in_database: 0,
      };
    } catch (error) {
      log('useGaleria', 'fetchImagesWithoutThumbnails', 'Error fetching images', error);
      throw error;
    }
  };

  /**
   * Generate thumbnail for a single image
   * @param {number} imageId - The image ID
   * @returns {Promise<Object>} Result with status (created, already_exists, error)
   */
  const generateSingleThumbnail = async (imageId: number): Promise<Galeria_GenerateSingleThumbnailResponseDados> => {
    log('useGaleria', 'generateSingleThumbnail', 'Generating thumbnail for image', imageId);
    try {
      const response = await httpService.get(`/index.php/galeria/generate_single_thumbnail/${imageId}`, { responseContract: 'Galeria_GenerateSingleThumbnailResponse' });
      log('useGaleria', 'generateSingleThumbnail', 'Thumbnail generated', response.dados);
      return response.dados || { id: imageId, nome: '', status: 'unknown', message: '' };
    } catch (error) {
      log('useGaleria', 'generateSingleThumbnail', 'Error generating thumbnail', error);
      throw error;
    }
  };

  /**
   * Cancel the current batch thumbnail generation
   */
  const cancelThumbnailGeneration = () => {
    log('useGaleria', 'cancelThumbnailGeneration', 'Cancelling thumbnail generation');
    thumbnailProgress.value.isCancelled = true;
  };

  /**
   * Reset thumbnail progress state
   */
  const resetThumbnailProgress = () => {
    thumbnailProgress.value = {
      isProcessing: false,
      current: 0,
      total: 0,
      created: 0,
      alreadyExist: 0,
      errors: 0,
      errorDetails: [],
      currentImageName: '',
      isCancelled: false
    };
  };

  /**
   * Generate thumbnails for all images without thumbnails using batch processing
   * Processes one image at a time with progress tracking
   * @returns {Promise<Object>} Final statistics about thumbnail generation
   */
  const generateMissingThumbnailsBatch = async () => {
    log('useGaleria', 'generateMissingThumbnailsBatch', 'Starting batch thumbnail generation with progress');

    // Reset and initialize progress
    resetThumbnailProgress();
    thumbnailProgress.value.isProcessing = true;

    try {
      // Step 1: Get list of images needing thumbnails
      const imagesData = await fetchImagesWithoutThumbnails();
      const images = imagesData.images || [];

      thumbnailProgress.value.total = images.length;
      thumbnailProgress.value.alreadyExist = imagesData.already_have_thumbnails || 0;

      log('useGaleria', 'generateMissingThumbnailsBatch', `Found ${images.length} images needing thumbnails`);

      if (images.length === 0) {
        thumbnailProgress.value.isProcessing = false;
        return {
          total_images: imagesData.total_in_database,
          thumbnails_created: 0,
          thumbnails_already_exist: imagesData.already_have_thumbnails,
          errors: 0,
          error_details: [],
          images_not_found: imagesData.images_not_found
        };
      }

      // Step 2: Process each image one by one
      for (let i = 0; i < images.length; i++) {
        // Check if cancelled
        if (thumbnailProgress.value.isCancelled) {
          log('useGaleria', 'generateMissingThumbnailsBatch', 'Generation cancelled by user');
          break;
        }

        const image = images[i];
        thumbnailProgress.value.current = i + 1;
        thumbnailProgress.value.currentImageName = image.nome;

        try {
          const result = await generateSingleThumbnail((image as any).id);

          if (result.status === 'created') {
            thumbnailProgress.value.created++;
          } else if (result.status === 'already_exists') {
            thumbnailProgress.value.alreadyExist++;
          }
        } catch (error) {
          thumbnailProgress.value.errors++;
          thumbnailProgress.value.errorDetails.push(
            `Erro na imagem ${image.nome}: ${(error as any).message || 'Erro desconhecido'}`
          );
          log('useGaleria', 'generateMissingThumbnailsBatch', `Error processing image ${image.id}`, error);
        }
      }

      // Build final stats
      const finalStats = {
        total_images: imagesData.total_in_database,
        thumbnails_created: thumbnailProgress.value.created,
        thumbnails_already_exist: thumbnailProgress.value.alreadyExist,
        errors: thumbnailProgress.value.errors,
        error_details: thumbnailProgress.value.errorDetails,
        images_not_found: imagesData.images_not_found,
        was_cancelled: thumbnailProgress.value.isCancelled
      };

      log('useGaleria', 'generateMissingThumbnailsBatch', 'Batch generation completed', finalStats);

      return finalStats;
    } catch (error) {
      log('useGaleria', 'generateMissingThumbnailsBatch', 'Error in batch generation', error);
      throw error;
    } finally {
      thumbnailProgress.value.isProcessing = false;
    }
  };

  /**
   * Generate thumbnails for all existing images that don't have thumbnails
   * Only accessible by Full permission group
   * @deprecated Use generateMissingThumbnailsBatch for progress tracking
   * @returns {Promise<Object>} Statistics about thumbnail generation
   */
  const generateMissingThumbnails = async () => {
    log('useGaleria', 'generateMissingThumbnails', 'Starting batch thumbnail generation (deprecated)');
    try {
      const response = await httpService.get('/index.php/galeria/generate_missing_thumbnails', { responseContract: 'Galeria_GenerateMissingThumbnailsResponse' });
      log('useGaleria', 'generateMissingThumbnails', 'Batch generation completed', response.dados);
      return response.dados || {};
    } catch (error) {
      log('useGaleria', 'generateMissingThumbnails', 'Error generating thumbnails', error);
      throw error;
    }
  };

  return {
    // State
    thumbnailProgress,
    // Methods
    fetchGaleriaImages,
    fetchAllServicoImages,
    fetchImagesWithoutThumbnails,
    generateSingleThumbnail,
    generateMissingThumbnails,
    generateMissingThumbnailsBatch,
    cancelThumbnailGeneration,
    resetThumbnailProgress
  };
}
